<script setup lang="ts">
import type { ProductHeaders } from '~/models/ProductHeaders'
import type { BaseItem } from '~/models/Content/BaseItem'
import { useRoute, useRouter }  from '#imports'
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import arrayUtils from '~/utils/arrayUtils'
import useProductStore from '~/stores/product'
import useFilterStore from '~/stores/filter'
import useSlugify from '~/composables/useSlugify'
import { useContentHelper } from '~/composables/useContentHelper'
import Select from '~/components/ui/Select.vue'

const props = withDefaults(defineProps<{
  resources?: BaseItem[]
  autoSelect?: boolean
  index: number
  enableGradeFilter?: boolean
  fullWidth?: boolean
  showNumberedHeaders?: boolean
  navigate?: boolean
  resettable?: boolean
  headerKey?: keyof ProductHeaders
}>(), {
  enableGradeFilter: true,
  showNumberedHeaders: true,
  headerKey: 'learningPath'
})

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const filterStore = useFilterStore()
const { contentFilters, setSelectedHeader, removeSelectedHeader } = filterStore
const { gradeFilterFunc } = contentFilters
const { productHeaders, isLoading, selectedHeaders } = storeToRefs(filterStore)
const { getProductFromResource } = useProductStore()
const { uniqueBy } = arrayUtils()
const { slugify } = useSlugify()

const numberedHeaders = computed(() => {
  if (props.resources)
    return []
  return productHeaders.value[props.headerKey] || []
})

const extraHeaders = computed(() => {
  if (props.resources)
    return props.resources
  const extraKey = props.headerKey === 'learningPath' ? 'flexibleContent' : props.headerKey
  return productHeaders.value[extraKey] || []
})

const { isHeader } = useContentHelper()

const allHeaders = computed(() => [...numberedHeaders.value, ...extraHeaders.value]
  .filter((header: BaseItem) => !props.enableGradeFilter || gradeFilterFunc(header))
  .filter(isHeader)
  .filter(uniqueBy('locationId')))

const showFilter = ref(true)
const hasHeaders = computed(() => !isLoading.value && showFilter.value && allHeaders.value.length > 0)

const onSelectUpdate = async (header: BaseItem | null) => {
  if (!header)
    return

  const product = getProductFromResource(header)
  const headerIndex = selectedHeaders.value
    .findIndex(({ locationId }) => locationId === header.locationId)

  if (props.navigate && product?.subjects.length) {
    await router.push({
      name: 'header',
      params: {
        subject: product.subjects[0].toLowerCase(),
        locationId: header.locationId,
        slug: slugify(header.title)
      },
      query: route.query,
    })
  }

  if (headerIndex > -1) {
    removeSelectedHeader(header)
    return
  }

  setSelectedHeader([header as any])
}

const selectedHeaderModel = computed<BaseItem | null>({
  get: () => {
    const storeHeader = selectedHeaders.value[props.index]
    if (!storeHeader)
      return null

    return allHeaders.value.find(h => h.locationId === storeHeader.locationId) ?? null
  },
  set: header => onSelectUpdate(header),
})

const getHeaderName = (header: BaseItem) => {
  if (props.showNumberedHeaders && productHeaders.value[props.headerKey]?.includes(header)) {
    const index = allHeaders.value.findIndex(({ locationId }) => locationId === header.locationId)
    return `${index + 1}   ${header.title}`
  }
  return header.title
}

watch([selectedHeaders, allHeaders], () => {
  showFilter.value = !(selectedHeaderModel.value && allHeaders.value.length === 0)
}, { immediate: true })
</script>

<template>
  <Select
    v-if="hasHeaders"
    v-model="selectedHeaderModel"
    :options="allHeaders"
    :option-label="getHeaderName"
    :placeholder="t('filters.chooseTheme')"
    variant="outline"
    size="lg"
  />
</template>
